import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import type { GoogleConnectRequest, GoogleConnectResponse } from "@/types/api";
import type { components } from "@/types/api.gen";

export interface ConnectArgs {
  customerId: string;
  template: string;
  displayName: string;
  scopes?: string[];
  loginHint?: string;
}

/**
 * Kick off the Google OAuth flow for one (customer, bot) pair. On
 * success we get back a Google authorize URL; the caller typically
 * `window.open`s it in a new tab so the user can click through
 * Google consent. The dashboard backend handles the callback,
 * provisions the Agents/<displayName> Drive folder, and writes the
 * encrypted refresh token + meta sidecar — no further action needed
 * on the frontend side.
 *
 * displayName is passed through so the backend knows what to name
 * the agent's private Drive folder. We use the live value (from the
 * org chart / agent config) so a rename in the org editor picks up
 * naturally on the next connect.
 */
export function useGoogleConnect() {
  return useMutation<GoogleConnectResponse, Error, ConnectArgs>({
    mutationFn: ({ customerId, template, displayName, scopes, loginHint }) =>
      apiFetch<GoogleConnectResponse>(
        `/api/customers/${customerId}/google/connect`,
        {
          method: "POST",
          body: {
            template,
            displayName,
            ...(scopes && scopes.length > 0 ? { scopes } : {}),
            ...(loginHint ? { loginHint } : {}),
          } satisfies GoogleConnectRequest,
        },
      ),
  });
}

// ─── status ───

export type GoogleStatusResponse = components["schemas"]["GoogleStatus"];
/** Why the agent sends from its address: chosen, or picked automatically. */
export type SendAsSource = NonNullable<GoogleStatusResponse["send_as_source"]>;
export type SendAsAlias = components["schemas"]["SendAsAliasOut"];
export type SendAsAliases = components["schemas"]["SendAsAliases"];

interface StatusScope {
  customerId: string;
  template: string;
}

/**
 * Live per-agent Google connection status. Cheap on the backend —
 * reads a single meta JSON file, no network. We refetch on window
 * focus so the state flips from "not connected" to "connected as
 * stanley@…" as soon as the user returns from the consent tab.
 */
export function useGoogleStatus(scope: StatusScope | null) {
  return useQuery({
    queryKey: ["google-status", scope?.customerId, scope?.template],
    enabled: scope !== null,
    queryFn: ({ signal }) =>
      apiFetch<GoogleStatusResponse>(
        `/api/customers/${scope!.customerId}/agents/${scope!.template}/google/status`,
        { signal },
      ),
    staleTime: 10_000,
    refetchOnWindowFocus: true,
  });
}

// ─── send-as aliases ───

/**
 * The account's verified send-as addresses (its primary plus every verified
 * "Send mail as" alias), which one is automatic for this agent and why.
 * Reading it costs a Gmail call and stores the automatic pick on the agent's
 * record, so it runs only when asked: the send-as dialog, and the Settings
 * tab once while the status says the list was never read ("unchecked").
 */
export function useGoogleAliases(scope: StatusScope | null, opts: { enabled?: boolean } = {}) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: ["google-aliases", scope?.customerId, scope?.template],
    enabled: scope !== null && opts.enabled === true,
    queryFn: async ({ signal }) => {
      const r = await apiFetch<SendAsAliases>(
        `/api/customers/${scope!.customerId}/agents/${scope!.template}/google/aliases`,
        { signal },
      );
      // The read stored the automatic pick: the status now knows it.
      void qc.invalidateQueries({ queryKey: ["google-status", scope!.customerId, scope!.template] });
      return r;
    },
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

// ─── calendar ───

export interface GoogleCalendarDateTime {
  date?: string;
  dateTime?: string;
  timeZone?: string;
}

export interface GoogleCalendarPerson {
  email?: string;
  displayName?: string;
  self?: boolean;
  responseStatus?: string;
}

export interface GoogleCalendarEvent {
  id: string;
  status?: string;
  summary: string;
  description?: string;
  location?: string;
  start: GoogleCalendarDateTime;
  end: GoogleCalendarDateTime;
  htmlLink?: string;
  meetLink?: string | null;
  organizer?: GoogleCalendarPerson;
  creator?: GoogleCalendarPerson;
  attendees?: GoogleCalendarPerson[];
  updated?: string;
}

interface GoogleCalendarEventsResponse {
  calendarId: string;
  timeZone?: string;
  events: GoogleCalendarEvent[];
}

interface CalendarEventsScope extends StatusScope {
  timeMin: string;
  timeMax: string;
  calendarId?: string;
  limit?: number;
}

export function useGoogleCalendarEvents(scope: CalendarEventsScope | null) {
  return useQuery({
    queryKey: [
      "google-calendar-events",
      scope?.customerId,
      scope?.template,
      scope?.calendarId ?? "primary",
      scope?.timeMin,
      scope?.timeMax,
      scope?.limit ?? 100,
    ],
    enabled: scope !== null,
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({
        calendarId: scope!.calendarId ?? "primary",
        timeMin: scope!.timeMin,
        timeMax: scope!.timeMax,
        limit: String(scope!.limit ?? 100),
      });
      return apiFetch<GoogleCalendarEventsResponse>(
        `/api/customers/${scope!.customerId}/agents/${scope!.template}/google/calendar/events?${params}`,
        { signal },
      );
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    retry: false,
  });
}

/** Choose the address the agent sends from; `email: null` = automatic again. */
export function useSetGoogleSendAs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      customerId,
      template,
      email,
      displayName,
    }: StatusScope & { email: string | null; displayName?: string }) =>
      apiFetch<components["schemas"]["SendAsResult"]>(
        `/api/customers/${customerId}/agents/${template}/google/send-as`,
        { method: "PUT", body: { email, displayName: displayName ?? "" } },
      ),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: ["google-status", vars.customerId, vars.template] });
      void qc.invalidateQueries({ queryKey: ["google-aliases", vars.customerId, vars.template] });
    },
  });
}

// ─── disconnect ───

export function useGoogleDisconnect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, template }: StatusScope) =>
      apiFetch<{ ok: boolean }>(
        `/api/customers/${customerId}/agents/${template}/google`,
        { method: "DELETE" },
      ),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({
        queryKey: ["google-status", vars.customerId, vars.template],
      });
    },
  });
}
